import { LockIcon, toastError, toastMsg } from '@devrangex/rui'
import styled from '@emotion/styled'
import { HTMLAttributes, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useRecoilState } from 'recoil'

import { qrBoardState } from '../../../../store/qr'
import { BayMachine, BayMachineOn, BayMachineUse, MACHINE_STATUS } from '../../../../utils'

import BayItemOff from './BayItemOff'
import BayItemOn from './BayItemOn'
import BayItemUse from './BayItemUse'


interface BayItemProps extends HTMLAttributes<HTMLDivElement> {
  bayMachine?: BayMachine
  bayItemSize?: 'lg' | 'md' | 'sm'
}

const StyledBayItem = styled.div`
  position: relative;
  flex-grow: 1;
  background-color: ${({theme}) => theme.background.base};
  width: 100%;
  height: 100%;
`

const Locker = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #000000cc;
  z-index: 1;

  svg {
    font-size: 48px !important;
  }
`

export default function BayItem({bayMachine, bayItemSize = 'md', ...others}: BayItemProps) {
  const [bayComp, setBayComp] = useState<ReactNode>()
  const [qrBoard, setQrBoard] = useRecoilState(qrBoardState)

  const onClickBay = useCallback(() => {
    if (!bayMachine || bayMachine.status !== MACHINE_STATUS.ON) return

    if (bayMachine.sensorManager?.isNotAvailable) {
      return toastError('타석에 오류가 있습니다.', {isKiosk: true})
    }

    setQrBoard({
      visible: true,
      machineName: bayMachine.name,
      loginCode: bayMachine.loginCode?.toString(),
    })
  }, [
    bayMachine?.status,
    bayMachine?.loginCode,
    bayMachine?.sensorManager?.isNotAvailable,
    setQrBoard,
  ])

  const onClickLock = useCallback(() => {
    toastMsg(
      '스마트로그인을 지원하지 않는 타석입니다.\n매니저에게 문의해주세요.',
      {isKiosk: true, alignCenter: true}
    )
  }, [])

  const closeQrBoard = useCallback(() => {
    setQrBoard({
      visible: false,
      machineName: undefined,
      loginCode: undefined,
    })
  }, [setQrBoard])

  useEffect(() => {
    if (!bayMachine) {
      setBayComp(undefined)
      return
    }

    switch (bayMachine?.status) {
    case MACHINE_STATUS.OFF:
      setBayComp(<BayItemOff name={bayMachine.name} size={bayItemSize} />)
      break

    case MACHINE_STATUS.ON:
      setBayComp(
        <BayItemOn
          {...(bayMachine as BayMachineOn)}
          size={bayItemSize}
          onClick={onClickBay}
        />
      )
      break

    default:
      setBayComp(<BayItemUse {...(bayMachine as BayMachineUse)} size={bayItemSize} />)
      break
    }
  }, [bayMachine, bayMachine?.status])

  // QR 보드 컨트롤
  useEffect(() => {
    if (!bayMachine) return

    const needToClose =
      qrBoard.loginCode === bayMachine.loginCode?.toString() &&
      (bayMachine.status !== MACHINE_STATUS.ON ||
        bayMachine.smartLoginState === 0)

    if (needToClose) {
      closeQrBoard()
    }

    return () => {
      closeQrBoard()
    }
  }, [bayMachine?.status, bayMachine?.smartLoginState])

  const isLocked = useMemo(() => {
    return (
      bayMachine?.status !== MACHINE_STATUS.OFF &&
      bayMachine?.smartLoginState === 0
    )
  }, [bayMachine?.status, bayMachine?.smartLoginState])

  return (
    <StyledBayItem {...others}>
      {bayComp}
      {isLocked && (
        <Locker data-testid="lock" onClick={onClickLock}>
          <LockIcon/>
        </Locker>
      )}
    </StyledBayItem>
  )
}
